import React, { useState, useEffect, useMemo } from "react";
import {
  DEFAULT_CATALOG_PRODUCTS,
  CATALOG_CATEGORIES
} from "../constants/catalogData";
import {
  subscribeToCatalogProducts,
  saveCatalogProductToFirestore,
  deleteCatalogProductFromFirestore,
} from "../services/firebaseService";
import ProductDetailModal from "../components/catalog/ProductDetailModal";
import CatalogManagerModal from "../components/catalog/CatalogManagerModal";

const LOCAL_STORAGE_KEY = "vittalink_catalog_products";
const FAVORITES_STORAGE_KEY = "vittalink_favorite_products";

export default function CatalogPage({ onNavigateHome }) {
  // Products state with LocalStorage fallback & Firestore sync
  const [products, setProducts] = useState(() => {
    try {
      const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch (e) {
      console.warn("Error parsing localStorage catalog:", e);
    }
    return DEFAULT_CATALOG_PRODUCTS;
  });

  // Favorites state
  const [favorites, setFavorites] = useState(() => {
    try {
      const saved = localStorage.getItem(FAVORITES_STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isManagerOpen, setIsManagerOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("Todos");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("default"); // default, rating-desc, rating-asc, name
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filterProteinOnly, setFilterProteinOnly] = useState(false);
  const [filterVeganOnly, setFilterVeganOnly] = useState(false);
  const [notification, setNotification] = useState(null);

  // Sync to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(products));
    } catch (e) {
      console.warn("Error writing to localStorage:", e);
    }
  }, [products]);

  // Sync favorites
  useEffect(() => {
    try {
      localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favorites));
    } catch (e) {
      console.warn("Error saving favorites:", e);
    }
  }, [favorites]);

  // Real-time Firestore sync
  useEffect(() => {
    let unsubscribe = () => {};
    try {
      unsubscribe = subscribeToCatalogProducts((firestoreProducts) => {
        setProducts(Array.isArray(firestoreProducts) ? firestoreProducts : []);
      });
    } catch (e) {
      console.warn("Firestore subscription error:", e);
    }
    return () => unsubscribe();
  }, []);

  const showToast = (msg) => {
    setNotification(msg);
    setTimeout(() => setNotification(null), 3500);
  };

  const toggleFavorite = (productId, e) => {
    if (e) e.stopPropagation();
    setFavorites((prev) => {
      const isFav = prev.includes(productId);
      const updated = isFav ? prev.filter((id) => id !== productId) : [...prev, productId];
      showToast(isFav ? "Removido de favoritos" : "♥ Agregado a tus favoritos");
      return updated;
    });
  };

  // Manager Handlers
  const handleSaveProduct = async (productToSave) => {
    setProducts((prev) => {
      const exists = prev.some((p) => p.id === productToSave.id);
      if (exists) {
        return prev.map((p) => (p.id === productToSave.id ? productToSave : p));
      }
      return [productToSave, ...prev];
    });

    saveCatalogProductToFirestore(productToSave);
    showToast(`✓ Producto "${productToSave.name}" guardado exitosamente`);
  };

  const handleDeleteProduct = async (productId) => {
    const prod = products.find((p) => p.id === productId);
    if (!prod) return;
    if (window.confirm(`¿Estás seguro de eliminar "${prod.name}" del catálogo?`)) {
      setProducts((prev) => prev.filter((p) => p.id !== productId));
      deleteCatalogProductFromFirestore(productId);
      showToast(`✓ Producto eliminado`);
    }
  };

  const handleToggleFeatured = async (productId) => {
    setProducts((prev) =>
      prev.map((p) => {
        if (p.id === productId) {
          const updated = { ...p, isFeatured: !p.isFeatured };
          saveCatalogProductToFirestore(updated);
          return updated;
        }
        return p;
      })
    );
  };

  const handleResetDefaults = () => {
    if (window.confirm("¿Restablecer el catálogo con los productos originales de VittaLink?")) {
      setProducts(DEFAULT_CATALOG_PRODUCTS);
      DEFAULT_CATALOG_PRODUCTS.forEach((p) => saveCatalogProductToFirestore(p));
      showToast("✓ Catálogo restaurado");
    }
  };

  // Filtered and Sorted Products
  const displayedProducts = useMemo(() => {
    let result = products.filter((p) => {
      // Category filter
      if (selectedCategory === "Destacados") {
        if (!p.isFeatured) return false;
      } else if (selectedCategory !== "Todos") {
        if (p.category !== selectedCategory) return false;
      }

      // Advanced filters
      if (filterProteinOnly) {
        const isProt = (p.tag && p.tag.toLowerCase().includes("prot")) ||
          (p.pills && p.pills.some(pill => pill.toLowerCase().includes("prot"))) ||
          p.category?.toLowerCase().includes("prot");
        if (!isProt) return false;
      }

      if (filterVeganOnly) {
        const isVeg = (p.ingredients && p.ingredients.toLowerCase().includes("vegan")) ||
          (p.allergens && p.allergens.toLowerCase().includes("vegan")) ||
          (p.pills && p.pills.some(pill => pill.toLowerCase().includes("plant") || pill.toLowerCase().includes("vegan")));
        if (!isVeg) return false;
      }

      // Search filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchName = p.name.toLowerCase().includes(q);
        const matchTag = p.tag && p.tag.toLowerCase().includes(q);
        const matchCat = p.category && p.category.toLowerCase().includes(q);
        const matchDesc = p.description && p.description.toLowerCase().includes(q);
        const matchIng = p.ingredients && p.ingredients.toLowerCase().includes(q);
        const matchLoc = (p.location || "").toLowerCase().includes(q);
        if (!matchName && !matchTag && !matchCat && !matchDesc && !matchIng && !matchLoc) return false;
      }

      return true;
    });

    // Sorting
    if (sortBy === "rating-desc") {
      result.sort((a, b) => Number(b.rating ?? 5) - Number(a.rating ?? 5));
    } else if (sortBy === "rating-asc") {
      result.sort((a, b) => Number(a.rating ?? 5) - Number(b.rating ?? 5));
    } else if (sortBy === "name") {
      result.sort((a, b) => a.name.localeCompare(b.name));
    }

    return result;
  }, [products, selectedCategory, searchQuery, sortBy, filterProteinOnly, filterVeganOnly]);

  const featuredCount = useMemo(() => {
    return products.filter((p) => p.isFeatured).length;
  }, [products]);

  // Category Icon Helper
  const getCategoryIcon = (index, active) => {
    const stroke = active ? "#c8f135" : "#64736a";
    return (
      <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {index === 0 && <><rect x="3" y="3" width="5" height="5" rx="1"/><rect x="12" y="3" width="5" height="5" rx="1"/><rect x="3" y="12" width="5" height="5" rx="1"/><rect x="12" y="12" width="5" height="5" rx="1"/></>}
        {index === 1 && <path d="m10 2.5 2.3 4.8 5.2.7-3.8 3.7.9 5.2-4.6-2.5-4.6 2.5.9-5.2-3.8-3.7 5.2-.7L10 2.5Z" fill={active ? "#c8f135" : "none"}/>}
        {index === 2 && <path d="M3 8v4m2-6v8m10-6v4m2-6v8M5 10h10"/>}
        {index === 3 && <><path d="M5 4h10l1 3-1.2 9H5.2L4 7l1-3Z"/><path d="M4.5 7h11M7 10h6m-5 3h4"/></>}
        {index === 4 && <><path d="M10 17.5c-3.5 0-6-3.1-6-6.9 0-3.5 2.1-6.2 5-6.2 1.2 0 2 .6 2.9.6.8 0 1.5-.6 2.5-.6 1.2 0 2.2.5 3 1.4-1.7 1-2.5 2.4-2.5 4.2 0 2 1.1 3.4 2.7 4.1-.9 2.2-2.5 3.6-4.1 3.6-1 0-1.7-.5-2.7-.5-.9 0-1.8.3-2.8.3Z"/><path d="M10 4c.1-1.6 1-2.6 2.8-3"/></>}
        {index === 5 && <><path d="M7 3h6v2l1 1v10a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V6l1-1V3Z"/><path d="M6 9h8m-7 3h6"/></>}
      </svg>
    );
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f4fbf5",
        color: "#0f2317",
        paddingTop: "94px",
        paddingBottom: "80px",
        fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
        position: "relative",
        overflowX: "hidden"
      }}
    >
      <style>{`
        /* Background subtle leaves decorative glow */
        .catalog-page-bg {
          background-image: 
            radial-gradient(900px 500px at 85% 15%, #e6f7e9 0%, transparent 65%),
            radial-gradient(700px 450px at 15% 45%, #eff9f0 0%, transparent 60%);
        }

        /* Ambient subtle leaf blur */
        .leaf-accent {
          position: absolute;
          border-radius: 0 100% 0 100%;
          background: linear-gradient(135deg, #a7e372, #4fa83a);
          filter: blur(8px);
          opacity: 0.16;
          pointer-events: none;
        }

        /* Category pills scroll on mobile */
        .cat-scroll-container {
          display: flex;
          gap: 8px;
          overflow-x: auto;
          scrollbar-width: none;
          padding-bottom: 2px;
        }
        .cat-scroll-container::-webkit-scrollbar {
          display: none;
        }

        /* Product Cards */
        .v-card {
          background: #ffffff;
          border-radius: 20px;
          border: 1px solid #e7f2e8;
          box-shadow: 0 4px 18px rgba(16, 50, 28, 0.03);
          transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.25s ease, border-color 0.25s ease;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          cursor: pointer;
        }
        .v-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 16px 32px rgba(16, 50, 28, 0.08);
          border-color: #bbf7d0;
        }
        .v-card:hover .v-card-img {
          transform: scale(1.04);
        }

        /* Animated Toast */
        @keyframes toastSlide {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>

      {/* Decorative leaf shapes */}
      <i className="leaf-accent" style={{ right: -40, top: 40, width: 240, height: 180, transform: "rotate(25deg)" }} />
      <i className="leaf-accent" style={{ left: -60, top: 420, width: 220, height: 160, transform: "rotate(-30deg)" }} />
      <i className="leaf-accent" style={{ right: -50, bottom: 200, width: 200, height: 150, transform: "rotate(45deg)" }} />

      {/* Toast Notification */}
      {notification && (
        <div
          style={{
            position: "fixed",
            bottom: "28px",
            right: "28px",
            zIndex: 3000,
            backgroundColor: "#14532d",
            color: "#ffffff",
            padding: "12px 24px",
            borderRadius: 9999,
            fontWeight: 700,
            fontSize: "13.5px",
            boxShadow: "0 10px 28px rgba(20, 83, 45, 0.28)",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            animation: "toastSlide 0.24s ease-out"
          }}
        >
          <span>✨</span> {notification}
        </div>
      )}

      {/* 3. ENCABEZADO EDITORIAL DEL CATÁLOGO (TWO COLUMNS) */}
      <section
        style={{
          width: "min(1280px, 94%)",
          margin: "0 auto 36px",
          display: "grid",
          gridTemplateColumns: "1.15fr 0.95fr",
          alignItems: "center",
          gap: "36px"
        }}
      >
        <style>{`
          @media (max-width: 960px) {
            section {
              grid-template-columns: 1fr !important;
              gap: 24px !important;
            }
            .hero-img-col {
              order: -1;
              max-width: 440px;
              margin: 0 auto;
            }
          }
        `}</style>

        {/* Columna Izquierda: Textos & Badges */}
        <div>

          {/* Título Principal */}
          <h1
            style={{
              fontSize: "clamp(32px, 4.2vw, 50px)",
              fontWeight: 900,
              lineHeight: 1.08,
              color: "#0f291e",
              letterSpacing: "-0.035em",
              margin: "0 0 16px 0"
            }}
          >
            Descubre los Snacks y Bebidas{" "}
            <span
              style={{
                display: "inline-block",
                background: "linear-gradient(90deg, #166534 0%, #22c55e 60%, #15803d 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent"
              }}
            >
              que transforman tu día
            </span>
          </h1>

          {/* Texto Descriptivo */}
          <p
            style={{
              fontSize: "15px",
              lineHeight: 1.6,
              color: "#475569",
              maxWidth: "520px",
              margin: "0 0 24px 0"
            }}
          >
            Cada producto en nuestras estaciones inteligentes cuenta con trazabilidad nutricional completa. Haz clic en cualquiera para consultar ingredientes, macronutrientes y espiral de retiro.
          </p>


        </div>

        {/* Columna Derecha: Composición Fotográfica */}
        <div
          className="hero-img-col"
          style={{
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          <div
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "520px"
            }}
          >
            <img
              src="/catalog/hero.jpg"
              alt="Snacks saludables VittaLink y Shake de Vainilla"
              style={{
                width: "100%",
                height: "auto",
                display: "block",
                borderRadius: "24px",
                filter: "drop-shadow(0 14px 28px rgba(22, 80, 40, 0.12))"
              }}
            />

            {/* Script Text Overlay */}
            <div
              style={{
                position: "absolute",
                top: "14px",
                right: "12px",
                textAlign: "right",
                pointerEvents: "none"
              }}
            >
              <span
                style={{
                  fontFamily: "Georgia, serif",
                  fontStyle: "italic",
                  fontSize: "15px",
                  color: "#166534",
                  fontWeight: 600,
                  display: "block",
                  lineHeight: 1.25
                }}
              >
                Energía<br />Nutrición<br />Bienestar
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* 4. BARRA DE FILTROS Y BÚSQUEDA MODERNA */}
      <div
        style={{
          width: "min(1280px, 94%)",
          margin: "0 auto 28px"
        }}
      >
        <div
          style={{
            backgroundColor: "#ffffff",
            borderRadius: 9999,
            padding: "8px 12px 8px 16px",
            border: "1px solid #e2ece3",
            boxShadow: "0 4px 18px rgba(16, 50, 28, 0.04)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            flexWrap: "wrap"
          }}
        >
          {/* Pestañas de categorías */}
          <div className="cat-scroll-container" style={{ flex: "1 1 auto", minWidth: "280px" }}>
            {CATALOG_CATEGORIES.map((cat, categoryIndex) => {
              const isActive = selectedCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  style={{
                    backgroundColor: isActive ? "#0f2e1b" : "transparent",
                    color: isActive ? "#ffffff" : "#475569",
                    border: "none",
                    borderRadius: 9999,
                    padding: "8px 16px",
                    fontSize: "13px",
                    fontWeight: 700,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    whiteSpace: "nowrap",
                    transition: "all 0.18s ease"
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) e.currentTarget.style.backgroundColor = "#f1f6f2";
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  <span style={{ display: "grid", placeItems: "center" }}>{getCategoryIcon(categoryIndex, isActive)}</span>
                  <span>{cat}</span>
                  {cat === "Destacados" && (
                    <span
                      style={{
                        backgroundColor: isActive ? "#22c55e" : "#e2ece3",
                        color: isActive ? "#0f2e1b" : "#475569",
                        borderRadius: 9999,
                        padding: "1px 6px",
                        fontSize: "11px",
                        fontWeight: 800,
                        marginLeft: "2px"
                      }}
                    >
                      {featuredCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Campo de búsqueda y botón filtros */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center"
              }}
            >
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="#839188" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true" style={{ position: "absolute", left: 12, pointerEvents: "none" }}><circle cx="8.7" cy="8.7" r="5.7"/><path d="m13 13 4 4"/></svg>
              <input
                type="text"
                placeholder="Buscar producto o ingrediente..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  backgroundColor: "#f7faf7",
                  border: "1px solid #e2ece3",
                  borderRadius: 9999,
                  padding: "8px 14px 8px 34px",
                  fontSize: "13px",
                  color: "#0f2317",
                  outline: "none",
                  width: "220px",
                  transition: "border-color 0.2s"
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#22c55e";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#e2ece3";
                }}
              />
              {searchQuery && (
                <button aria-label="Limpiar búsqueda" onClick={() => setSearchQuery("")} style={{ position: "absolute", right: 10, border: "none", background: "transparent", color: "#64736a", cursor: "pointer", display: "grid", placeItems: "center", padding: 4 }}><svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true"><path d="m4 4 8 8m0-8-8 8"/></svg></button>
              )}
            </div>

            {/* Botón Filtros Avanzados */}
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              style={{
                backgroundColor: showAdvancedFilters ? "#dcfce7" : "#f7faf7",
                border: `1px solid ${showAdvancedFilters ? "#86efac" : "#e2ece3"}`,
                borderRadius: "50%",
                width: 36,
                height: 36,
                display: "grid",
                placeItems: "center",
                cursor: "pointer",
                color: "#166534",
                fontSize: "14px",
                transition: "all 0.2s"
              }}
              title="Filtros avanzados y ordenamiento"
            ><svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true"><path d="M3 5h14M5.5 10h9M8 15h4"/><circle cx="7" cy="5" r="1.2" fill="currentColor"/><circle cx="12.5" cy="10" r="1.2" fill="currentColor"/><circle cx="10" cy="15" r="1.2" fill="currentColor"/></svg></button>
          </div>
        </div>

        {/* Panel Desplegable de Filtros Avanzados */}
        {showAdvancedFilters && (
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: 18,
              padding: "16px 20px",
              marginTop: "10px",
              border: "1px solid #e2ece3",
              boxShadow: "0 6px 20px rgba(16, 50, 28, 0.05)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "16px",
              flexWrap: "wrap",
              animation: "toastSlide 0.2s ease-out"
            }}
          >
            <div style={{ display: "flex", gap: "16px", alignItems: "center", flexWrap: "wrap" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", color: "#334155", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={filterProteinOnly}
                  onChange={(e) => setFilterProteinOnly(e.target.checked)}
                  style={{ accentColor: "#16a34a" }}
                />
                <span>Solo altos en proteína</span>
              </label>

              <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", color: "#334155", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={filterVeganOnly}
                  onChange={(e) => setFilterVeganOnly(e.target.checked)}
                  style={{ accentColor: "#16a34a" }}
                />
                <span>Solo veganos / plant-based</span>
              </label>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ fontSize: "12.5px", color: "#64748b" }}>Ordenar por:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  backgroundColor: "#f7faf7",
                  border: "1px solid #e2ece3",
                  borderRadius: 10,
                  padding: "6px 12px",
                  fontSize: "12.5px",
                  color: "#1e293b",
                  outline: "none"
                }}
              >
                <option value="default">Recomendados</option>
                <option value="rating-desc">Mejor valorados</option>
                <option value="rating-asc">Menor valoración</option>
                <option value="name">Nombre (A - Z)</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* 5. TÍTULO DE SECCIÓN: «Productos más populares» Y CONTADOR */}
      <div
        style={{
          width: "min(1280px, 94%)",
          margin: "0 auto 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "17px" }}>🔥</span>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 800,
              color: "#0f291e",
              letterSpacing: "-0.02em",
              margin: 0
            }}
          >
            {selectedCategory === "Todos" ? "Productos más populares" : `Productos en ${selectedCategory}`}
          </h2>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span style={{ fontSize: "12.5px", color: "#64748b", fontWeight: 600 }}>
            ★ {displayedProducts.length} productos {selectedCategory === "Destacados" ? "destacados" : "disponibles"}
          </span>

          {/* Quick navigation arrows (visual affordance) */}
          <div style={{ display: "flex", gap: "6px" }}>
            <button
              onClick={() => {
                const el = document.getElementById("catalog-products-grid");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                border: "1px solid #e2ece3",
                backgroundColor: "#ffffff",
                display: "grid",
                placeItems: "center",
                cursor: "pointer",
                fontSize: "11px",
                color: "#166534"
              }}
              title="Anterior"
            >
              〈
            </button>
            <button
              onClick={() => {
                const el = document.getElementById("catalog-products-grid");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                border: "1px solid #e2ece3",
                backgroundColor: "#ffffff",
                display: "grid",
                placeItems: "center",
                cursor: "pointer",
                fontSize: "11px",
                color: "#166534"
              }}
              title="Siguiente"
            >
              〉
            </button>
          </div>
        </div>
      </div>

      {/* 6. CUADRÍCULA DE TARJETAS DE PRODUCTOS */}
      <div
        id="catalog-products-grid"
        style={{
          width: "min(1280px, 94%)",
          margin: "0 auto 48px"
        }}
      >
        {displayedProducts.length === 0 ? (
          <div
            style={{
              padding: "70px 20px",
              textAlign: "center",
              backgroundColor: "#ffffff",
              borderRadius: 24,
              border: "1px dashed #bbf7d0",
              boxShadow: "0 4px 18px rgba(16, 50, 28, 0.03)"
            }}
          >
            <div style={{ fontSize: "42px", marginBottom: "12px" }}>🥗</div>
            <h3 style={{ fontSize: "18px", fontWeight: 800, color: "#0f291e", marginBottom: "6px" }}>
              No se encontraron productos coincidentes
            </h3>
            <p style={{ fontSize: "13.5px", color: "#64748b", maxWidth: "380px", margin: "0 auto 18px" }}>
              Intenta borrar el término de búsqueda o seleccionar otra categoría del menú superior.
            </p>
            <button
              onClick={() => {
                setSelectedCategory("Todos");
                setSearchQuery("");
                setFilterProteinOnly(false);
                setFilterVeganOnly(false);
              }}
              style={{
                backgroundColor: "#166534",
                color: "#ffffff",
                border: "none",
                padding: "10px 22px",
                borderRadius: 9999,
                fontWeight: 700,
                fontSize: "13px",
                cursor: "pointer"
              }}
            >
              Ver todos los productos
            </button>
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
              gap: "20px"
            }}
          >
            {displayedProducts.map((p) => {
              const isFav = favorites.includes(p.id);
              const pills = p.pills || (p.tag ? [p.tag] : []);

              // Top badge color configuration
              let badgeBg = "#bbf7d0";
              let badgeColor = "#14532d";
              let badgeBorder = "#86efac";

              if (p.badge === "MÁS VENDIDO") {
                badgeBg = "#bbf7d0";
                badgeColor = "#14532d";
              } else if (p.badge === "ALTO EN PROTEÍNA") {
                badgeBg = "#bbf7d0";
                badgeColor = "#14532d";
              } else if (p.badge === "ANTIOXIDANTES") {
                badgeBg = "#bbf7d0";
                badgeColor = "#14532d";
              }

              return (
                <article
                  key={p.id}
                  className="v-card"
                  onClick={() => setSelectedProduct(p)}
                >
                  {/* Top Image Container */}
                  <div
                    style={{
                      position: "relative",
                      width: "100%",
                      aspectRatio: "1/0.95",
                      overflow: "hidden",
                      backgroundColor: "#f7faf7"
                    }}
                  >
                    <img
                      src={p.img}
                      alt={p.name}
                      className="v-card-img"
                      loading="lazy"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        transition: "transform 0.35s ease"
                      }}
                      onError={(e) => {
                        e.target.src = "/catalog/hero.jpg";
                      }}
                    />

                    {/* Top Left Badge */}
                    {p.badge && (
                      <div
                        style={{
                          position: "absolute",
                          top: 10,
                          left: 10,
                          backgroundColor: badgeBg,
                          color: badgeColor,
                          border: `1px solid ${badgeBorder}`,
                          padding: "4px 10px",
                          borderRadius: 9999,
                          fontSize: "10px",
                          fontWeight: 800,
                          letterSpacing: "0.04em",
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
                        }}
                      >
                        <span>★</span> {p.badge}
                      </div>
                    )}

                    {/* Top Right Favorite Heart */}
                    <button
                      type="button"
                      onClick={(e) => toggleFavorite(p.id, e)}
                      aria-label="Agregar a favoritos"
                      style={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        backgroundColor: "rgba(255, 255, 255, 0.92)",
                        border: "1px solid #e2ece3",
                        display: "grid",
                        placeItems: "center",
                        cursor: "pointer",
                        fontSize: "15px",
                        color: isFav ? "#ef4444" : "#94a3b8",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                        transition: "transform 0.18s ease"
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.1)"}
                      onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
                    >
                      {isFav ? "♥" : "♡"}
                    </button>

                    {/* Nutritional Pills below image */}
                    <div
                      style={{
                        position: "absolute",
                        bottom: 8,
                        left: 8,
                        display: "flex",
                        gap: "5px",
                        flexWrap: "wrap"
                      }}
                    >
                      {pills.map((pill, idx) => (
                        <span
                          key={idx}
                          style={{
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            color: "#166534",
                            border: "1px solid #bbf7d0",
                            padding: "3px 8px",
                            borderRadius: 9999,
                            fontSize: "10px",
                            fontWeight: 700,
                            backdropFilter: "blur(4px)"
                          }}
                        >
                          {pill}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Card Body */}
                  <div
                    style={{
                      padding: "16px",
                      display: "flex",
                      flexDirection: "column",
                      flexGrow: 1,
                      gap: "6px"
                    }}
                  >
                    {/* Title */}
                    <h3
                      style={{
                        fontSize: "15.5px",
                        fontWeight: 800,
                        color: "#0f291e",
                        lineHeight: 1.25,
                        margin: 0
                      }}
                    >
                      {p.name}
                    </h3>

                    {/* Category subtitle */}
                    <span style={{ fontSize: "11.5px", color: "#64748b", fontWeight: 600 }}>
                      {p.category}
                    </span>

                    {/* Short description */}
                    <p
                      style={{
                        fontSize: "12px",
                        color: "#64748b",
                        lineHeight: 1.45,
                        margin: "2px 0 0 0",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden"
                      }}
                    >
                      {p.shortDesc || p.description}
                    </p>

                    {/* Vending location */}
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#94a3b8",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        marginTop: "4px"
                      }}
                    >
                      <span style={{ color: "#ef4444", fontSize: "12px" }}>📍</span>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {p.location || (Array.isArray(p.machines) ? p.machines[0] : "Máquinas VittaLink")}
                      </span>
                    </div>

                    {/* Card Footer: Rating & Product Details */}
                    <div
                      style={{
                        marginTop: "auto",
                        paddingTop: "12px",
                        borderTop: "1px solid #f1f5f2",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                      }}
                    >
                      <span aria-label={`${Number(p.rating ?? 5).toFixed(1)} de 5 estrellas`} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 14, fontWeight: 800, color: "#334155", whiteSpace: "nowrap" }}>
                        <span style={{ color: "#eab308", letterSpacing: 1 }}>{Array.from({ length: 5 }, (_, i) => i < Math.round(Number(p.rating ?? 5)) ? "★" : "☆").join("")}</span>
                        {Number(p.rating ?? 5).toFixed(1)}
                      </span>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedProduct(p);
                        }}
                        style={{
                          backgroundColor: "#166534",
                          color: "#ffffff",
                          border: "none",
                          padding: "7px 14px",
                          borderRadius: 9999,
                          fontSize: "12px",
                          fontWeight: 700,
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "5px",
                          boxShadow: "0 2px 8px rgba(22, 101, 52, 0.2)",
                          transition: "all 0.18s ease"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#15803d";
                          e.currentTarget.style.transform = "scale(1.03)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "#166534";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      >
                        Ver detalle
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>



      {/* MODAL DETALLES DEL PRODUCTO */}
      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onOpenManager={(p) => {
            setEditingProduct(p);
            setIsManagerOpen(true);
          }}
          onToggleFavorite={toggleFavorite}
          isFavorite={favorites.includes(selectedProduct.id)}
        />
      )}

      {/* MODAL MANAGER DE PRODUCTOS */}
      <CatalogManagerModal
        isOpen={isManagerOpen}
        onClose={() => {
          setIsManagerOpen(false);
          setEditingProduct(null);
        }}
        products={products}
        onSaveProduct={handleSaveProduct}
        onDeleteProduct={handleDeleteProduct}
        onToggleFeatured={handleToggleFeatured}
        onResetDefaults={handleResetDefaults}
        editingProduct={editingProduct}
        setEditingProduct={setEditingProduct}
      />
    </div>
  );
}
